import React, { ReactElement, useState } from 'react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import useAuth from '../auth/authContext';
import { AppRoute, AppRoutes } from '../router/AppRoutes';
import VisibleResource from './VisibleResource';
import {
  AiStarsIcon,
  BriefcaseIcon,
  BulbIcon,
  CalendarIcon,
  FundingIcon,
  LogoGrowegy,
  PowerIcon,
  SettingsIcon,
  ShoppingBagIcon,
} from '../assets/icons';
import clsx from 'clsx';
import {
  useIsNewVersionPersonalRecommendations,
  usePersonalRecommendations,
} from '../recommendations/hooks/usePersonalRecommendations';
import { ICurrentUser } from '../currentuser/currentUser';
import { Permission } from '../auth/permissions';
import {
  businessPlanConfig,
  fundingConfig,
  growegyAIConfig,
  marketplaceConfig,
  mobileConfig,
  pushNotificationsConfig,
} from '../commons/config';
import { StyleVariables } from '../commons/styleConstants';
import { useHasFetchedPastDueInvoices } from '../billing/hooks/useFetchedBillingInvoices';
import { useIsSmallerThanTablet, useIsTabletOrLarger } from '../responsive/hooks/hooks';
import { GrowegyMenu } from '../commons/components/GrowegyMenu';
import { UserAvatar } from '../commons/components/UserAvatar';
import { useCurrentUser } from '../user/hooks/useCurrentUser';
import { extractUserName } from '../user/utils';
import { useNewReferrals } from '../marketplace/hooks/useNewReferrals';

import { OnStartModalPrompt } from '../releaseprompt/components/OnStartModalPrompt';
import { createTutorialsMenuItem } from '../help/components/TutorialMenuItem';
import { createReleasesMenuItem } from '../help/components/ReleaseMenuItem';
import { createSupportMenuItem } from '../help/components/SupportMenuItem';
import { StickyNote } from './StickyNote';
import { useAppSelector } from '../app/hooks';
import { useUpdateOnboardingProgressMutation } from '../onboarding/hooks/useUpdateOnboardingProgressMutation';
import { useFetchedOnboardingProgress } from '../onboarding/hooks/useFetchedOnboardingProgress';
import { ONBOARDING_STATE_NOT_OFFERED, ONBOARDING_STATE_NOT_STARTED } from '../user/userApi';
import { restartOnboardingMenuItem } from '../help/components/RestartOnboardingMenuItem';
import { analyticsTrack } from '../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../web-analytics/AnalyticsEvent';
import { PushNotificationsTokenSync } from '../push-notifications/components/PushNotificationsTokenSync';
import { useSignOut } from '../auth/hooks/useSignOut';
import { createAppStoreMenuItem } from '../help/components/AppStoreMenuItem';
import { v4 as uuidv4 } from 'uuid';
import { createPlayStoreMenuItem } from '../help/components/PlayStoreMenuItem';
import { DownloadAppModal } from '../help/components/DownloadAppModal';
import { billingMenuItem } from '../help/components/BillingMenuItem';
import { useNewFundingPartners } from '../funding/hooks/useNewFundingPartners';

const SideBarItem = (props: {
  header: string;
  icon: (props: { fill: string }) => ReactElement;
  isActive: boolean;
  subheader?: string;
  badge?: string | null;
}) => {
  const { header, icon, isActive, subheader, badge } = props;
  const color = isActive ? StyleVariables.neutral900 : StyleVariables.neutral500;
  return (
    <div className="app-sidebar__item">
      <div
        className={clsx('app-sidebar__item-icon', { active: isActive })}
        data-test="app-sidebar__item-icon"
      >
        {icon({ fill: color })}
        {badge && (
          <div className="app-sidebar__item-indicator-container">
            <div className="app-sidebar__item-indicator">
              <span
                className="app-sidebar__item-indicator-number"
                data-test="app-sidebar__badge-value"
              >
                {badge}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="app-sidebar__item-header" data-test="side-bar__link-header" style={{ color }}>
        {header}
      </div>
      {subheader ? <div className="app-sidebar__item-badge">{subheader}</div> : null}
    </div>
  );
};

const SideBarLink = (props: {
  to: AppRoute;
  alternativeTo?: AppRoute;
  secondAlternativeTo?: AppRoute;
  header: string;
  icon: (props: { fill: string }) => ReactElement;
  subheader?: string;
  badge?: string | null;
  stickyNoteContent?: ReactElement | null;
}) => {
  const {
    to,
    alternativeTo,
    secondAlternativeTo,
    header,
    icon,
    subheader,
    badge,
    stickyNoteContent,
  } = props;
  const isPrimaryActive = useMatch(to)!!;
  const isAlternativeActive = useMatch(alternativeTo ?? 'not-matching-path');
  const isSecondAlternativeToActive = useMatch(secondAlternativeTo ?? 'not-matching-path');
  const isActive = !!(
    isPrimaryActive ||
    (alternativeTo && isAlternativeActive) ||
    (secondAlternativeTo && isSecondAlternativeToActive)
  );
  return (
    <NavLink className="side-bar__link" data-test="side-bar__link" to={to}>
      <SideBarItem
        header={header}
        icon={icon}
        isActive={isActive}
        subheader={subheader}
        badge={badge}
      />
      {stickyNoteContent && <StickyNote>{stickyNoteContent}</StickyNote>}
    </NavLink>
  );
};

const SideBarLogo = () => (
  <div className="app-sidebar__logo" data-test="app-sidebar__logo">
    <img src={LogoGrowegy} alt="" />
  </div>
);

const SideBarPlanning = (props: { user: ICurrentUser }) => {
  const { user } = props;
  const hasGrowegyAiPermission = user && user.has(Permission.USE_CHAT_GPT);
  return (
    <VisibleResource
      to={hasGrowegyAiPermission ? AppRoute.calendar : AppRoute.home}
      render={(to) => {
        return (
          <SideBarLink
            to={to}
            alternativeTo={hasGrowegyAiPermission ? undefined : AppRoute.calendar}
            header="Planning"
            icon={(props: { fill: string }) => <CalendarIcon fill={props.fill} />}
          />
        );
      }}
    />
  );
};

const SideBarBusinessPlan = (props: { user: ICurrentUser; isTabletOrLarger: boolean }) => {
  const { user, isTabletOrLarger } = props;
  if (!user?.has(Permission.BUSINESS_PLAN)) return null;

  return (
    <VisibleResource
      to={AppRoute.businessPlan}
      render={(to) => (
        <SideBarLink
          to={to}
          header={isTabletOrLarger ? 'Business Plan' : 'Biz Plan'}
          icon={(props: { fill: string }) => <BriefcaseIcon fill={props.fill} />}
        />
      )}
    />
  );
};

const SideBarGrowegyAI = (props: { user: ICurrentUser; isTabletOrLarger: boolean }) => {
  const { user, isTabletOrLarger } = props;
  const { justCompleted } = useAppSelector((state) => state.onboarding);
  if (!growegyAIConfig.enabled || !user.has(Permission.USE_CHAT_GPT)) return null;

  return (
    <VisibleResource
      to={AppRoutes.Home}
      render={(to) => (
        <SideBarLink
          to={to}
          alternativeTo={AppRoutes.GrowegyAI}
          header={isTabletOrLarger ? 'Growegy AI' : 'AI'}
          icon={(props: { fill: string }) => <AiStarsIcon fill={props.fill} />}
          stickyNoteContent={
            justCompleted ? (
              <div>
                Thanks for completing onboarding.
                <br />
                You are currently here.
              </div>
            ) : null
          }
        />
      )}
    />
  );
};

const SideBarRecommendations = (props: { user: ICurrentUser }) => {
  const { user } = props;
  const hasNewRecommendations = useIsNewVersionPersonalRecommendations();
  const { data: recommendations } = usePersonalRecommendations('');

  if (
    !user.has(Permission.GET_TENANT_RECOMMENDATIONS) ||
    !recommendations ||
    recommendations.recommendations.length === 0
  )
    return null;

  return (
    <VisibleResource
      to={AppRoutes.Recommendations}
      render={(to) => (
        <SideBarLink
          to={to}
          header="Insights"
          icon={(props: { fill: string }) => <BulbIcon fill={props.fill} />}
          badge={hasNewRecommendations ? '1' : null}
        />
      )}
    />
  );
};

const SideBarFunding = (props: { user: ICurrentUser; isTabletOrLarger: boolean }) => {
  const { user, isTabletOrLarger } = props;
  const { newFundingPartners } = useNewFundingPartners();

  if (
    !user.has(Permission.GET_TENANT_FUNDING_SETTINGS) ||
    !user.has(Permission.REPLACE_TENANT_FUNDING_SETTINGS)
  ) {
    return null;
  }

  return (
    <VisibleResource
      to={AppRoutes.Funding}
      render={(to) => (
        <SideBarLink
          to={to}
          header={isTabletOrLarger ? 'Funding' : 'Fund'}
          icon={(props: { fill: string }) => <FundingIcon fill={props.fill} stroke={props.fill} />}
          badge={
            newFundingPartners.length > 0 ? `${Math.min(newFundingPartners.length, 99)}` : null
          }
        />
      )}
    />
  );
};

const SideBarMarketplace = () => {
  const { newReferrals } = useNewReferrals();
  const isSmallerThanTablet = useIsSmallerThanTablet();
  return (
    <VisibleResource
      to={AppRoutes.Marketplace}
      render={(to) => (
        <SideBarLink
          to={to}
          header={isSmallerThanTablet ? 'Market' : 'Marketplace'}
          icon={(props: { fill: string }) => <ShoppingBagIcon fill={props.fill} />}
          badge={newReferrals.length > 0 ? `${Math.min(newReferrals.length, 99)}` : null}
        />
      )}
    />
  );
};

const SideBarSettings = () => {
  const hasPastDueInvoices = useHasFetchedPastDueInvoices();
  return (
    <VisibleResource
      to={AppRoutes.Settings.root}
      render={(to) => (
        <SideBarLink
          to={to}
          secondAlternativeTo={AppRoute.settings}
          alternativeTo={AppRoute.billing}
          header="Settings"
          icon={(props: { fill: string }) => <SettingsIcon fill={props.fill} />}
          badge={hasPastDueInvoices ? '1' : null}
        />
      )}
    />
  );
};

const SideBarNavigation = (props: { user: ICurrentUser }) => {
  const { user } = props;
  const isTabletOrLarger = useIsTabletOrLarger();

  return (
    <nav className="app-sidebar__navigation">
      <div className="app-sidebar__navigation-items">
        <SideBarGrowegyAI user={user} isTabletOrLarger={isTabletOrLarger} />
        <SideBarPlanning user={user} />
        {businessPlanConfig.enabled && (
          <SideBarBusinessPlan user={user} isTabletOrLarger={isTabletOrLarger} />
        )}
        {fundingConfig.enabled && (
          <SideBarFunding user={user} isTabletOrLarger={isTabletOrLarger} />
        )}
        {marketplaceConfig.enabled && <SideBarMarketplace />}
        {isTabletOrLarger && <SideBarRecommendations user={user} />}
        <SideBarSettings />
        {!isTabletOrLarger && <SideBarAccount />}
      </div>
    </nav>
  );
};

export const SideBarAccount = (props: { signOutOnly?: boolean }) => {
  const { signOutOnly } = props;
  const isTabletOrLarger = useIsTabletOrLarger();
  const user = useCurrentUser();
  const {
    state: { user: currentUser },
  } = useAuth();
  const {
    query: { data: onboardingProgress },
  } = useFetchedOnboardingProgress();

  const { mutate: updateOnboardingProgress } = useUpdateOnboardingProgressMutation();
  const onboardingCouldBeRestarted =
    !signOutOnly &&
    !!onboardingProgress &&
    onboardingProgress.state !== ONBOARDING_STATE_NOT_OFFERED &&
    onboardingProgress.state !== ONBOARDING_STATE_NOT_STARTED;

  const { signOut } = useSignOut();
  const [downloadOnAppStoreKey, setDownloadOnAppStoreKey] = useState<string | null>(null);
  const [getOnPlayStoreKey, setGetOnPlayStoreKey] = useState<string | null>(null);
  const navigate = useNavigate();

  return (
    <>
      <VisibleResource
        to={AppRoute.home}
        render={() => (
          <GrowegyMenu
            menuItems={[
              {
                type: 'custom',
                dataTest: 'app-sidebar__account-container',
                component: () => (
                  <div className="app-sidebar__account-container">
                    <div className="app-sidebar__account-info-container">
                      <span>{extractUserName(user)}</span>
                      <span className="app-sidebar__account-role">{user.role.name}</span>
                    </div>
                    <div className="app-sidebar__account-email">{user.email}</div>
                  </div>
                ),
              },
              {
                type: 'divider',
                dataTest: 'app-sidebar__account-container-divider',
                isHidden: signOutOnly,
              },
              billingMenuItem(!currentUser?.has(Permission.CHANGE_BILLING) || !!signOutOnly, () =>
                navigate(AppRoute.billing)
              ),
              restartOnboardingMenuItem(!onboardingCouldBeRestarted, () => {
                analyticsTrack(AnalyticsEvent.ONBOARDING_RESTARTED);
                updateOnboardingProgress({
                  state: ONBOARDING_STATE_NOT_STARTED,
                  steps: [],
                });
              }),
              {
                type: 'divider',
                dataTest: 'app-sidebar__account-help-divider',
              },
              createAppStoreMenuItem(
                false,
                () => {
                  setDownloadOnAppStoreKey(uuidv4());
                  setGetOnPlayStoreKey(null);
                },
                'app-sidebar__help-app-store'
              ),
              createPlayStoreMenuItem(
                false,
                () => {
                  setDownloadOnAppStoreKey(null);
                  setGetOnPlayStoreKey(uuidv4());
                },
                'app-sidebar__help-play-store'
              ),
              {
                type: 'divider',
                dataTest: 'app-sidebar__apps-divider',
              },
              createTutorialsMenuItem(false, 'app-sidebar__account-tutorials'),
              createReleasesMenuItem(false, 'app-sidebar__account-releases'),
              createSupportMenuItem(false, 'app-sidebar__account-support'),
              {
                type: 'divider',
                dataTest: 'app-sidebar__account-sign-out-divider',
              },
              {
                type: 'standard',
                text: 'Sign out',
                icon: <PowerIcon />,
                dataTest: 'app-sidebar__account-sign-out',
                onClick: signOut,
              },
            ]}
            menuButtonDataTest="side-bar__link-button"
            containerDataTest="side-bar__link"
            containerClassName="side-bar__link"
            drop={isTabletOrLarger ? 'right' : 'up'}
            positionStrategy="fixed"
            customToggle={() => (
              <SideBarItem
                header="Account"
                icon={() => <UserAvatar user={user} />}
                isActive={false}
              />
            )}
          />
        )}
      />
      {downloadOnAppStoreKey && <DownloadAppModal store="app-store" key={downloadOnAppStoreKey} />}
      {getOnPlayStoreKey && <DownloadAppModal store="play-store" key={getOnPlayStoreKey} />}
    </>
  );
};

export const SideBar = () => {
  const {
    state: { user },
  } = useAuth();
  const isTabletOrLarger = useIsTabletOrLarger();

  return (
    user && (
      <div className="app-sidebar" data-test="app-sidebar">
        <SideBarLogo />
        <SideBarNavigation user={user} />
        {isTabletOrLarger && (
          <div className="app-sidebar__navigation-items" style={{ flex: '0 0 auto' }}>
            <SideBarAccount />
          </div>
        )}
        <OnStartModalPrompt />
        {mobileConfig().isMobile && pushNotificationsConfig.isEnabled && (
          <PushNotificationsTokenSync />
        )}
      </div>
    )
  );
};
